import React, { useMemo, useRef, useState } from "react";
import financeV2Adapters from "../../../../adapters/financeV2Adapters";
import styles from "./FinanceurRail.module.scss";

const fmtEur = (n) => `${Number(n || 0).toLocaleString("fr-FR")} €`;

/**
 * Rail gauche de la page Financeurs : résumé global, recherche, bouton de
 * création et liste des financeurs avec leur capacité disponible cumulée.
 */
const FinanceurRail = ({
  financiers,
  groupes,
  selectedUuid,
  onSelect,
  onCreateStart,
}) => {
  const [query, setQuery] = useState("");
  const listRef = useRef(null);

  // ── Résumé global (groupes ayant au moins une enveloppe active) ──
  const totaux = useMemo(() => {
    return Object.values(groupes).reduce(
      (acc, g) => {
        const hasActive = (g.enveloppes || []).some((e) => e.estActive);
        if (!hasActive) return acc;
        const c = g.consommation;
        acc.origine += c.enveloppeOrigine;
        acc.dispo += c.disponible;
        acc.consomme += c.reserve + c.engage;
        return acc;
      },
      { origine: 0, dispo: 0, consomme: 0 },
    );
  }, [groupes]);

  const globalConso = financeV2Adapters.enveloppeRatio(
    totaux.consomme,
    totaux.origine,
  );
  const globalDispoRatio = financeV2Adapters.enveloppeRatio(
    totaux.dispo,
    totaux.origine,
  );
  const globalColor = financeV2Adapters.enveloppeColor(globalConso);
  const globalColorText = financeV2Adapters.enveloppeColorText(globalConso);

  const filtered = financiers.filter((f) =>
    (f.name || "").toLowerCase().includes(query.trim().toLowerCase()),
  );

  // Navigation clavier : flèches haut/bas entre items.
  const onKeyDown = (e) => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    e.preventDefault();
    const items = Array.from(
      listRef.current?.querySelectorAll("[data-rail-item]") || [],
    );
    const idx = items.indexOf(document.activeElement);
    const next =
      e.key === "ArrowDown"
        ? Math.min(idx + 1, items.length - 1)
        : Math.max(idx - 1, 0);
    items[next]?.focus();
  };

  return (
    <div className={styles.rail}>
      {/* ── Résumé global ── */}
      {totaux.origine > 0 && (
        <div className={styles.summary}>
          <div className={styles.summaryLabel}>Disponible — toutes enveloppes</div>
          <div className={styles.summaryValue} style={{ color: globalColorText }}>
            {fmtEur(totaux.dispo)}
          </div>
          <div className={styles.summaryBar}>
            <div
              className={styles.summaryBarFill}
              style={{ width: `${globalDispoRatio}%`, background: globalColor }}
            />
          </div>
          <div className={styles.summaryMeta}>
            sur {fmtEur(totaux.origine)} alloués · {Math.round(globalConso)} % consommé
          </div>
        </div>
      )}

      {/* ── Barre d'outils ── */}
      <div className={styles.toolbar}>
        <button type="button" className={styles.addBtn} onClick={onCreateStart}>
          <i className="fa fa-plus" /> Nouveau financeur
        </button>
        <div className={styles.search}>
          <i className="fa fa-search" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Rechercher un financeur…"
            aria-label="Rechercher un financeur"
          />
        </div>
      </div>

      {/* ── Liste ── */}
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <div
        className={styles.list}
        ref={listRef}
        role="listbox"
        aria-label="Financeurs"
        onKeyDown={onKeyDown}
      >
        {filtered.length === 0 ? (
          <div className={styles.noResult}>Aucun financeur trouvé.</div>
        ) : (
          filtered.map((f) => {
            const g = groupes[f.uuid];
            const hasActive = (g?.enveloppes || []).some((e) => e.estActive);
            const origine = g?.consommation?.enveloppeOrigine ?? 0;
            const dispo = g?.consommation?.disponible ?? 0;
            const consomme =
              (g?.consommation?.reserve ?? 0) + (g?.consommation?.engage ?? 0);
            const ratio = financeV2Adapters.enveloppeRatio(consomme, origine);
            const dispoRatio = financeV2Adapters.enveloppeRatio(dispo, origine);
            const color = financeV2Adapters.enveloppeColor(ratio);
            const colorText = financeV2Adapters.enveloppeColorText(ratio);
            const epuisee = hasActive && origine > 0 && dispo <= 0;
            const isActive = f.uuid === selectedUuid;
            return (
              <button
                type="button"
                key={f.uuid}
                data-rail-item
                role="option"
                aria-selected={isActive}
                aria-current={isActive ? "true" : undefined}
                className={`${styles.item} ${isActive ? styles.itemActive : ""}`}
                onClick={() => onSelect(f.uuid)}
              >
                <div className={styles.itemTop}>
                  <span className={styles.itemName}>{f.name}</span>
                  {hasActive && (
                    <span className={styles.itemDispo} style={{ color: colorText }}>
                      {fmtEur(dispo)}
                    </span>
                  )}
                </div>
                {hasActive ? (
                  epuisee ? (
                    <div className={styles.itemEpuisee}>
                      <i className="fa fa-exclamation-triangle" /> Épuisée
                    </div>
                  ) : (
                    <div className={styles.miniBar}>
                      <div
                        className={styles.miniBarFill}
                        style={{ width: `${dispoRatio}%`, background: color }}
                      />
                    </div>
                  )
                ) : (
                  <div className={styles.itemEmpty}>Aucune enveloppe active</div>
                )}
              </button>
            );
          })
        )}
      </div>
      <div className={styles.count}>
        {financiers.length} financeur{financiers.length > 1 ? "s" : ""}
      </div>
    </div>
  );
};

export default FinanceurRail;
